import React, { useState, useRef, useEffect } from 'react';
import { X, Send, CheckCircle, Loader2 } from 'lucide-react';
import { useContactModal } from '../context/ContactModalContext';
import { isValidPhone, PHONE_ERROR_MESSAGE } from '../utils/validatePhone';
import TermsAcceptCheckbox from './forms/TermsAcceptCheckbox';

const ContactModal = () => {
  const { isOpen, formData: prefillData, closeModal } = useContactModal();
  const modalRef = useRef(null);
  
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (isOpen && prefillData) {
      setFormState(prev => ({
        ...prev,
        service: prefillData.service || prev.service,
        message: prefillData.prefilledMessage || prev.message,
      }));
    }
  }, [isOpen, prefillData]);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setFormState({ name: '', email: '', phone: '', service: '', message: '' });
        setSubmitStatus(null);
        setErrors({});
        setTermsAccepted(false);
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) closeModal();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeModal]);

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) closeModal();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formState.name.trim()) newErrors.name = 'Required';
    if (!formState.email.trim()) newErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) newErrors.email = 'Invalid email';
    if (!formState.phone.trim()) newErrors.phone = 'Required';
    else if (!isValidPhone(formState.phone)) newErrors.phone = PHONE_ERROR_MESSAGE;
    if (!termsAccepted) newErrors.terms = 'Please accept the Terms and Privacy Policy to continue.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          phone: formState.phone,
          service: formState.service,
          message: formState.message,
          source: prefillData.source || 'Website',
          privacy_consent: true,
          source_page: typeof window !== 'undefined' ? window.location.pathname : null,
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Submission failed');
      }

      // OpenAI Ads conversion: fire `lead_created` ONLY after a successful
      // One-on-One Support enquiry submission. Gate mirrors the scope
      // statement above so we track the exact same intent surface.
      const isOneOnOneSupport =
        prefillData?.source === 'OneOnOneSupportPage' ||
        (prefillData?.service || '').toLowerCase().includes('one on one');
      if (isOneOnOneSupport && typeof window !== 'undefined' && typeof window.oaiq === 'function') {
        try {
          window.oaiq('measure', 'lead_created', { type: 'customer_action' });
        } catch (pixelErr) {
          // Never let pixel errors break the success UX.
          console.warn('oaiq lead_created failed:', pixelErr);
        }
      }

      setSubmitStatus('success');
      setTimeout(() => closeModal(), 2000);
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = [
    { value: '', label: 'Select...' },
    { value: 'general', label: 'General Enquiry' },
    { value: 'building-a-new-home', label: 'New Home' },
    { value: 'renovations', label: 'Renovations' },
    { value: 'choosing-land', label: 'Land Selection' },
    { value: 'changing-builders', label: 'Changing Builders' },
    { value: 'property-development', label: 'Property Development' },
    { value: 'swimming-pools', label: 'Swimming Pools' },
  ];

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={handleBackdropClick}
      data-testid="contact-modal-backdrop"
    >
      <div 
        ref={modalRef}
        className="relative w-full max-w-xl bg-white/90 bp-glass-surface bp-glass-surface--strong bp-glass-edge rounded-2xl shadow-2xl overflow-hidden animate-slideUp"
        data-testid="contact-modal"
      >
        {/* Header - Compact */}
        <div className="relative bg-[#1d1d1f] px-5 py-4">
          <button
            onClick={closeModal}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            aria-label="Close modal"
            data-testid="close-modal-btn"
          >
            <X className="w-4 h-4" />
          </button>
          
          <h2 className="text-lg font-bold text-white pr-8">
            Let's Talk About Your Project
          </h2>
          <p className="text-white/60 text-xs">
            {prefillData?.source === 'OneOnOneSupportPage-nonSA'
              ? 'Online enquiry'
              : 'Adelaide & South Australia'}
          </p>
        </div>

        {/* One on One Support scope statement - appears at top of intake
            for the premium advisor service so users know upfront that
            BuildPilot's guidance is not a substitute for licensed
            financial, legal, engineering or design advice (ACCC s33
            substantiation + ACL §18 implied warranty risk). */}
        {(prefillData?.source === 'OneOnOneSupportPage' ||
          (prefillData?.service || '').toLowerCase().includes('one on one')) && (
          <div
            className="px-5 py-3 border-b"
            style={{
              background: 'var(--bp-color-surface-tinted)',
              borderColor: 'var(--bp-color-hairline)',
            }}
            data-testid="o3-scope-statement"
          >
            <p
              className="text-[10px] font-semibold uppercase mb-1"
              style={{ color: 'var(--bp-color-blue)', letterSpacing: '0.18em' }}
            >
              Scope
            </p>
            <p
              className="text-[12.5px] leading-[1.55] m-0"
              style={{ color: 'var(--bp-color-ink)' }}
            >
              General building guidance only. We are not licensed financial, legal, engineering or design advisers &mdash; consult an appropriately qualified professional for those matters.
            </p>
          </div>
        )}
        
        {/* Success State */}
        {submitStatus === 'success' ? (
          <div className="px-6 py-10 text-center">
            <div className="w-14 h-14 rounded-full bg-[var(--bp-color-surface-tinted)] flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-[#1d1d1f] mb-1">Thank You!</h3>
            <p className="text-[#86868b] text-sm">We'll be in touch as soon as we can.</p>
          </div>
        ) : (
          /* Form - Compact Layout */
          <form onSubmit={handleSubmit} className="p-4 sm:p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-[#1d1d1f] mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formState.name}
                  onChange={handleInputChange}
                  placeholder="John Smith"
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${errors.name ? 'border-red-400 bg-[var(--bp-color-surface-tinted)]' : 'border-gray-200 bg-[#f5f5f7]'} text-[#1d1d1f] placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent`}
                  data-testid="name-input"
                />
              </div>
              
              {/* Phone */}
              <div>
                <label className="block text-xs font-medium text-[#1d1d1f] mb-1">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formState.phone}
                  onChange={handleInputChange}
                  placeholder="0412 345 678"
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${errors.phone ? 'border-red-400 bg-[var(--bp-color-surface-tinted)]' : 'border-gray-200 bg-[#f5f5f7]'} text-[#1d1d1f] placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent`}
                  data-testid="phone-input"
                />
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-[#1d1d1f] mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formState.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm ${errors.email ? 'border-red-400 bg-[var(--bp-color-surface-tinted)]' : 'border-gray-200 bg-[#f5f5f7]'} text-[#1d1d1f] placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent`}
                  data-testid="email-input"
                />
              </div>
              
              {/* Service */}
              <div>
                <label className="block text-xs font-medium text-[#1d1d1f] mb-1">How can we help?</label>
                <select
                  name="service"
                  value={formState.service}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-[#f5f5f7] text-[#1d1d1f] text-sm focus:outline-none focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent"
                  data-testid="service-select"
                >
                  {serviceOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Message - More space */}
            <div className="mt-3">
              <label className="block text-xs font-medium text-[#1d1d1f] mb-1">
                Message <span className="text-[#86868b] font-normal">(optional)</span>
              </label>
              <textarea
                name="message"
                value={formState.message}
                onChange={handleInputChange}
                rows={3}
                placeholder="Tell us about your project..."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-[#f5f5f7] text-[#1d1d1f] placeholder-[#86868b] text-sm focus:outline-none focus:ring-2 focus:ring-[#1d8efe] focus:border-transparent resize-none"
                data-testid="message-textarea"
              />
            </div>
              
            {/* Submit Button - Full width */}
            <div className="mt-4 space-y-3">
              <TermsAcceptCheckbox
                checked={termsAccepted}
                onChange={(v) => { setTermsAccepted(v); if (errors.terms) setErrors(prev => ({ ...prev, terms: '' })); }}
                testId="contact-modal-terms"
              />
              {errors.terms && (
                <p className="text-red-500 text-xs" data-testid="contact-modal-terms-error">{errors.terms}</p>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium text-white gradient-orange-bg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Enquiry
                  </>
                )}
              </button>
            </div>
            
            {/* Errors */}
            {submitStatus === 'error' && (
              <div className="mt-3 text-center">
                <p className="text-red-500 text-xs">
                  Something went wrong. Please try again.
                </p>
              </div>
            )}
            
            {/* Privacy Note */}
            <p className="text-[10px] text-center text-[#86868b] mt-3">
              Read our full <a href="/terms" className="text-[#1d8efe] hover:underline">Terms</a> &amp;{' '}
              <a href="/privacy" className="text-[#1d8efe] hover:underline">Privacy Policy</a>.
              We'll be in touch as soon as we can.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
